const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3010';

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
};

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const res = await fetch(`${API_URL}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include'
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new ApiError(res.status, error.message || 'Request failed');
  }

  return res.json();
}

/** Ensures the result is an array, returns empty array if not */
async function requestArray<T>(endpoint: string, options: RequestOptions = {}): Promise<T[]> {
  const result = await request<T[]>(endpoint, options);
  return Array.isArray(result) ? result : [];
}

// Letters API
interface PaginatedLetters {
  letters: Letter[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const letters = {
  createDraft: (data: {
    recipientId: string;
    subject: string;
    content: unknown;
    replyToId?: string;
  }) => request<{ id: string }>('/letters', { method: 'POST', body: data }),

  updateDraft: (id: string, data: { subject?: string; content?: unknown }) =>
    request(`/letters/${id}`, { method: 'PUT', body: data }),

  deleteDraft: (id: string) => request(`/letters/${id}`, { method: 'DELETE' }),

  send: (id: string) => request<{ deliveredAt: string }>(`/letters/${id}/send`, { method: 'POST' }),

  getInbox: async () => {
    const result = await request<PaginatedLetters>('/letters/inbox');
    return result.letters ?? [];
  },

  getSent: async () => {
    const result = await request<PaginatedLetters>('/letters/sent');
    return result.letters ?? [];
  },

  getDrafts: () => requestArray<Letter>('/letters/drafts'),

  getInTransit: () => requestArray<Letter>('/letters/in-transit'),

  getById: (id: string) => request<Letter>(`/letters/${id}`),

  getThread: (id: string) => requestArray<Letter>(`/letters/${id}/thread`),

  getDeliveryEstimate: (recipientId: string) =>
    request<{ delayDays: number; estimatedDelivery: string }>(`/letters/estimate/${recipientId}`)
};

// Auth API
export const auth = {
  me: () => request<{ user: User }>('/auth/me'),

  usernameAvailable: (username: string) =>
    request<{ available: boolean }>(`/auth/username-available/${username}`)
};

// Users API
export const users = {
  getProfile: () => request<UserProfile>('/users/me'),

  updateProfile: (data: {
    bio?: string;
    locale?: string;
    interestIds?: string[];
    displayName?: string | null;
    signatureText?: string | null;
    signatureFont?: SignatureFont;
    letterFont?: SignatureFont;
  }) => request<UserProfile>('/users/me', { method: 'PUT', body: data }),

  deleteAccount: (password: string) =>
    request<{ success: boolean }>('/users/me', { method: 'DELETE', body: { password } }),

  exportData: async () => {
    const res = await fetch(`${API_URL}/users/me/export`, { credentials: 'include' });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Request failed' }));
      throw new ApiError(res.status, error.message || 'Request failed');
    }
    const disposition = res.headers.get('Content-Disposition');
    const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'caneta-export.json';
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  getPublicProfile: (username: string) => request<PublicProfile>(`/users/${username}`),

  search: (query: string, limit?: number) =>
    requestArray<PublicProfile>(
      `/users/search?q=${encodeURIComponent(query)}${limit ? `&limit=${limit}` : ''}`
    ),

  getSuggested: (limit?: number) =>
    requestArray<SuggestedUser>(`/users/suggested${limit ? `?limit=${limit}` : ''}`),

  getByInterest: (interestId: string, limit?: number) =>
    requestArray<PublicProfile>(`/users/by-interest/${interestId}${limit ? `?limit=${limit}` : ''}`)
};

// Interests API
export const interests = {
  getAll: () => requestArray<Interest>('/interests'),

  getGrouped: () => request<Record<string, Interest[]>>('/interests/grouped'),

  getCategories: () => requestArray<string>('/interests/categories'),

  seed: () => request<{ seeded: number }>('/interests/seed', { method: 'POST' })
};

// Types
export type SignatureFont =
  | 'CAVEAT'
  | 'DANCING_SCRIPT'
  | 'PACIFICO'
  | 'INDIE_FLOWER'
  | 'HOMEMADE_APPLE';

export const SIGNATURE_FONTS: { value: SignatureFont; label: string; cssFamily: string }[] = [
  { value: 'CAVEAT', label: 'Caveat', cssFamily: "'Caveat', cursive" },
  { value: 'DANCING_SCRIPT', label: 'Dancing Script', cssFamily: "'Dancing Script', cursive" },
  { value: 'PACIFICO', label: 'Pacifico', cssFamily: "'Pacifico', cursive" },
  { value: 'INDIE_FLOWER', label: 'Indie Flower', cssFamily: "'Indie Flower', cursive" },
  { value: 'HOMEMADE_APPLE', label: 'Homemade Apple', cssFamily: "'Homemade Apple', cursive" }
];

export function getSignatureFontFamily(font: SignatureFont): string {
  const fontConfig = SIGNATURE_FONTS.find((f) => f.value === font);
  return fontConfig?.cssFamily || "'Caveat', cursive";
}

/**
 * Get display name for a user, falling back to username if not set
 */
export function getDisplayName(user: { displayName?: string | null; username: string }): string {
  return user.displayName || user.username;
}

/**
 * Format user for display: "Display Name (@username)" or just "@username"
 */
export function formatUserDisplay(user: { displayName?: string | null; username: string }): string {
  if (user.displayName) {
    return `${user.displayName} (@${user.username})`;
  }
  return `@${user.username}`;
}

export interface Interest {
  id: string;
  name: string;
  category: string;
}

export interface User {
  id: string;
  email: string;
  username: string;
  displayName?: string | null;
  country: string;
  locale: string;
  bio?: string;
  signatureText?: string | null;
  signatureFont?: SignatureFont;
  letterFont?: SignatureFont;
  emailVerified: boolean;
  createdAt: string;
}

export interface UserProfile extends User {
  displayNameUpdatedAt?: string | null;
  interests: Interest[];
}

export interface PublicProfile {
  id: string;
  username: string;
  displayName?: string | null;
  country: string;
  bio?: string;
  createdAt: string;
  interests: Interest[];
}

export interface SuggestedUser extends PublicProfile {
  sharedInterests: number;
}

export interface Letter {
  id: string;
  senderId: string;
  recipientId: string;
  subject: string;
  content: unknown;
  status: 'DRAFT' | 'IN_TRANSIT' | 'DELIVERED' | 'READ';
  sentAt?: string;
  deliveredAt?: string;
  readAt?: string;
  replyToId?: string;
  createdAt: string;
  sender?: User;
  recipient?: User;
  // Sender info captured at send time
  senderDisplayName?: string | null;
  senderSignatureText?: string | null;
  senderSignatureFont?: SignatureFont | null;
  senderLetterFont?: SignatureFont | null;
}

export { ApiError };
